import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  FiHome,
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiSettings,
  FiChevronDown,
  FiSearch,
  FiArrowLeft,
} from "react-icons/fi";
import {
  FaUserCircle,
  FaMoneyBillWave,
  FaNetworkWired,
  FaCrown,
} from "react-icons/fa";
import { AiOutlineIdcard, AiFillStar } from "react-icons/ai";
import {
  BsFileEarmarkText,
  BsBox,
  BsMoon,
  BsSun,
  BsGiftFill,
} from "react-icons/bs";
import { HiUserGroup, HiSparkles } from "react-icons/hi";
import { GiWallet, GiDiamondTrophy } from "react-icons/gi";
import { BiTransfer } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import { RiCoinsLine } from "react-icons/ri";
import { MdOutlineEditCalendar, MdPersonAdd } from "react-icons/md";
import { sendApiRequest } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import { setValue } from "../features/UserInfo/userSlice";

const DashboardLayout = ({ isRefresh, setIsRefresh }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user_info[0]?.user);
  const dispatch = useDispatch();

  useEffect(() => {
    menuItems.forEach((item, index) => {
      if (
        item.submenu &&
        item.submenu.some((sub) => location.pathname === sub.path)
      ) {
        setActiveSubmenu(index);
      }
    });
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    setIsRefresh("logagain");
    dispatch(setValue("Logout"));
    navigate("/login");
  };

  const menuItems = [
    {
      path: "/dashboard",
      icon: <FiHome className="w-[18px] h-[18px]" />,
      title: "Home",
    },
    {
      path: "",
      icon: <FaUserCircle className="w-[18px] h-[18px]" />,
      title: "My Profile",
      submenu: [
        { path: "/dashboard/show-profile", title: "Show Profile" },
        { path: "/dashboard/update-profile", title: "Update Profile" },
        {
          path: "/dashboard/update-login-password",
          title: "Update Login Password",
        },
      ],
    },
    {
      path: "/dashboard/update-kyc",
      icon: <AiOutlineIdcard className="w-[18px] h-[18px]" />,
      title: "Update KYC",
    },
    {
      path: "",
      icon: <BsFileEarmarkText className="w-[18px] h-[18px]" />,
      title: "Plan & Letters",
      submenu: [
        { path: "/dashboard/show-business-plan", title: "Business Plan Chart" },
      ],
    },
    {
      path: "",
      icon: <BsBox className="w-[18px] h-[18px]" />,
      title: "Package",
      submenu: [
        { path: "/dashboard/buy-package", title: "Buy Package" },
        { path: "/dashboard/list-invoices", title: "List Invoices" },
      ],
    },
    {
      path: "",
      icon: <HiUserGroup className="w-[18px] h-[18px]" />,
      title: "My Team",
      submenu: [
        { path: "/dashboard/my-direct-network", title: "Direct Network" },
        { path: "/dashboard/my-total-network", title: "Total Network" },
      ],
    },
    {
      path: "/dashboard/network-explorer",
      icon: <FaNetworkWired className="w-[18px] h-[18px]" />,
      title: "Network Explorer",
    },
    {
      path: "/dashboard/my-rewards",
      icon: <BsGiftFill className="w-[18px] h-[18px]" />,
      title: "My Rewards",
    },
    {
      path: "",
      icon: <GiWallet className="w-[18px] h-[18px]" />,
      title: "Fund Manager",
      submenu: [
        { path: "/dashboard/withdrawal-request", title: "Withdrawal Request" },
        { path: "/dashboard/withdrawal-status", title: "Withdrawal Status" },
      ],
    },
  ];

  const toggleSubmenu = (index) => {
    setActiveSubmenu(activeSubmenu === index ? null : index);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const isItemActive = (item) =>
    location.pathname === item.path ||
    (item.submenu &&
      item.submenu.some((sub) => location.pathname === sub.path));

  return (
    <div className="flex h-screen bg-[#F0F4FF] overflow-hidden">
      {/* ── Mobile backdrop ── */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-20 bg-black lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════
          SIDEBAR
      ═══════════════════════════════ */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: sidebarOpen ? 0 : -300 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className="fixed inset-y-0 left-0 z-30 w-[260px] flex flex-col bg-white border-r border-[#E4EBFF] shadow-[4px_0_24px_rgba(37,35,98,0.06)] lg:static lg:translate-x-0"
        style={{ height: "100vh" }}
      >
        {/* ── Brand bar ── */}

        {/* ── User card ── */}
        <div className="mx-3 mt-4 mb-2 rounded-xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] p-4 text-white shadow-lg shadow-indigo-200/60">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <img
                src={userData?.profile_image}
                alt="Profile"
                className="w-11 h-11 rounded-xl object-cover ring-2 ring-white/30"
              />
              <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate leading-tight">
                {userData?.fullName}
              </p>
              <p className="text-[11px] text-indigo-200 mt-0.5">
                ID: {userData?.sponsorCode}
              </p>
            </div>
            <div className="ml-auto shrink-0">
              <span className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-sm px-2 py-1 rounded-full text-[10px] font-semibold border border-white/20">
                <FaCrown className="w-2.5 h-2.5 text-yellow-300" />
                {userData?.rank || "Basic"}
              </span>
            </div>
          </div>
        </div>

        {/* ── Nav menu ── */}
        <nav
          className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5"
          style={{ scrollbarWidth: "none" }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-3 mb-2">
            Navigation
          </p>

          {menuItems.map((item, index) => {
            const active = isItemActive(item);
            return (
              <div key={index}>
                <Link
                  to={item.path}
                  onClick={(e) => {
                    if (item.submenu) {
                      e.preventDefault();
                      toggleSubmenu(index);
                    } else {
                      handleNavClick();
                    }
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-all duration-150 group
                    ${
                      active
                        ? "bg-[#EEF2FF] text-[#4F46E5]"
                        : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                >
                  {/* Icon pill */}
                  <span
                    className={`flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-150
                    ${
                      active
                        ? "bg-[#4F46E5] text-white shadow-sm shadow-indigo-300"
                        : "bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700"
                    }`}
                  >
                    {item.icon}
                  </span>

                  <span className="flex-1 leading-none">{item.title}</span>

                  {/* Active pill / chevron */}
                  {item.submenu ? (
                    <FiChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        activeSubmenu === index ? "rotate-180" : ""
                      } ${active ? "text-[#4F46E5]" : "text-gray-400"}`}
                    />
                  ) : (
                    active && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                    )
                  )}
                </Link>

                {/* Submenu */}
                <AnimatePresence initial={false}>
                  {item.submenu && activeSubmenu === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-[38px] mt-0.5 mb-1 border-l-2 border-[#E4EBFF] pl-3 space-y-0.5">
                        {item.submenu.map((sub) => {
                          const subActive = location.pathname === sub.path;
                          return (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              onClick={handleNavClick}
                              className={`block py-2 px-2 rounded-lg text-[12.5px] transition-all duration-150
                                ${
                                  subActive
                                    ? "text-[#4F46E5] font-semibold bg-[#EEF2FF]"
                                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                                }`}
                            >
                              {sub.title}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* ── Logout ── */}
        <div className="p-3 border-t border-[#E4EBFF] shrink-0">
          <button
            onClick={() => {
              handleLogout();
              handleNavClick();
            }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[13.5px] font-medium text-red-400 hover:bg-red-50 hover:text-red-600 transition-all duration-150 group"
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-red-50 group-hover:bg-red-100 transition-colors duration-150">
              <FiLogOut className="w-[17px] h-[17px]" />
            </span>
            Sign out
          </button>
        </div>
      </motion.aside>

      {/* ═══════════════════════════════
          MAIN COLUMN
      ═══════════════════════════════ */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* ── Top header ── */}
        <header className="shrink-0 flex items-center justify-between h-[64px] px-5 bg-white border-b border-[#E4EBFF] shadow-sm z-10">
          {/* Left: burger + back */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors"
            >
              <FiMenu className="w-5 h-5" />
            </button>

            {!location.pathname.match(/^\/dashboard$/) && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 hover:text-[#4F46E5] transition-colors"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-[#EEF2FF]">
                  <FiArrowLeft className="w-4 h-4" />
                </div>
                <span className="hidden sm:block">Back</span>
              </button>
            )}

            {/* Breadcrumb page title */}
            <span className="hidden md:block text-[13px] font-semibold text-[#1E1B4B] pl-1">
              {menuItems.find(
                (m) =>
                  m.path === location.pathname ||
                  (m.submenu &&
                    m.submenu.some((s) => s.path === location.pathname))
              )?.title ?? "Dashboard"}
            </span>
          </div>

          {/* Right: rank chip + avatar */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 bg-[#EEF2FF] text-[#4F46E5] text-[12px] font-semibold px-3 py-1.5 rounded-full">
              <GiDiamondTrophy className="w-3.5 h-3.5" />
              {userData?.rank || "Basic"}
            </span>

            {/* Avatar */}
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              className="relative"
            >
              <img
                src={userData?.profile_image}
                alt="Avatar"
                className="w-9 h-9 rounded-xl object-cover ring-2 ring-[#E4EBFF] hover:ring-[#4F46E5] transition-all duration-150"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white" />
            </button>
          </div>
        </header>

        {/* ── Scrollable main area ── */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F0F4FF]">
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
              className="bg-white rounded-2xl shadow-sm border border-[#E4EBFF] p-5 sm:p-7 min-h-[calc(100vh-120px)]"
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;